import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { CustomButton, TextField } from '../../../v2/MUI_custom_component';
import GlassModal from '../../../v2/MUI_custom_component/Modal/GlassModal';
import { selectMOQ } from '../../DP/CreateDP/reducer/DpSelectors';

const 
EditPOModal = ({ 
    open, 
    onClose, 
    poDetails, 
    selectedSupplierData,
    onSave,
    loading = false 
}) => {
    const [editQuantity, setEditQuantity] = useState('');
    const [editUserPONumber, setEditUserPONumber] = useState('');
    const [quantityError, setQuantityError] = useState('');
    const selectMOQData = useSelector(selectMOQ);

    const roundToThreeDecimals = (num) => {
        return Math.round((num + Number.EPSILON) * 1000) / 1000;
    };

    const validateQuantity = (quantity) => {
        const numQuantity = parseFloat(quantity);
        if (isNaN(numQuantity) || numQuantity <= 0) {
            setQuantityError('Please enter a valid quantity');
            return false;
        }
        
        if (selectMOQData?.max_po_quantity && typeof selectMOQData.max_po_quantity === 'number' && selectMOQData.max_po_quantity > 0) {
            if (numQuantity > selectMOQData.max_po_quantity) {
                setQuantityError(`PO quantity cannot exceed ${roundToThreeDecimals(selectMOQData.max_po_quantity)} tons`);
                return false;
            }
        }
        
        setQuantityError('');
        return true;
    };

    const handleQuantityChange = (e) => {
        const value = e.target.value;
        setEditQuantity(value);
        
        // Clear error when user starts typing
        if (quantityError) {
            setQuantityError('');
        }
        
        // Validate on change if value exists
        if (value && value.trim() !== '') {
            validateQuantity(value);
        }
    };

    // Initialize form fields when modal opens or poDetails changes
    useEffect(() => {
        if (open && poDetails) {
            setEditQuantity(poDetails.quantity_info?.[0]?.value || '');
            setEditUserPONumber(poDetails.user_po_number || '');
            setQuantityError(''); // Clear any previous errors
        }
    }, [open, poDetails]);

    const handleSave = () => {
        if (!editQuantity || editQuantity.trim() === '') {
            setQuantityError('Please enter a quantity');
            return;
        }
        
        if (!validateQuantity(editQuantity)) {
            return;
        }
        
        if (onSave) {
            onSave({
                quantity: editQuantity,
                userPONumber: editUserPONumber,
                poDetails
            });
        }
    };

    const handleClose = () => {
        // Reset form fields
        setEditQuantity('');
        setEditUserPONumber('');
        setQuantityError('');
        onClose();
    };

    return (
        <GlassModal
            open={open}
            onClose={handleClose}
            title="Edit Purchase Orders"
            scrollable={true}
            maxWidth="600px"
            zIndex={2}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Typography variant="labelM" sx={{ color: '#666' }}>
                            SAP Code*
                        </Typography>
                        <Typography variant="Body1">
                            {selectedSupplierData && selectedSupplierData.entity_info[0]?.external_system_code}
                        </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <TextField
                            placeholder="Enter Quantity in tons"
                            variant="standard"
                            fullWidth
                            label={`Quantity(in Tons)*`}
                            value={editQuantity}
                            onChange={handleQuantityChange}
                            error={!!quantityError}
                            helperText={quantityError}
                            type="number"
                            inputProps={{ 
                                min: selectMOQData?.min_po_quantity || 0, 
                                max: selectMOQData?.max_po_quantity && selectMOQData.max_po_quantity > 0 ? selectMOQData.max_po_quantity : undefined,
                                step: 0.001
                            }}
                        />
                    </Box>
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Typography variant="labelM" sx={{ color: '#666' }}>
                            User PO No.
                        </Typography>
                        <TextField
                            variant="standard"
                            size="small"
                            value={editUserPONumber}
                            onChange={(e) => setEditUserPONumber(e.target.value)}
                            placeholder="Enter PO number"
                            fullWidth
                        />
                    </Box>
                </Box>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h2">
                        Are you sure you want to update PO?
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#666' }}>
                        Your purchase order will be updated.
                    </Typography>
                </Box>
                
                <Box sx={{
                    display: 'flex',
                    gap: 1,
                }}>
                    <CustomButton
                        label="No"
                        variant="outlined"
                        color="primary"
                        onClick={handleClose}
                        sx={{ 
                            width: '50%',
                        }}
                    />
                    <CustomButton
                        label="Yes"
                        variant="filled"
                        color="primary"
                        loading={loading}
                        onClick={handleSave}
                        sx={{ 
                            width: '50%',
                        }}
                        disabled={quantityError}
                    />
                </Box>
            </Box>
        </GlassModal>
    );
};

export default EditPOModal;